import { useHubSpotPipelineStages } from "@/api/integrations/hubspot";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { HubspotPipelineStageEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, List, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { optionsAre } from "./testers";

interface HubSpotPipelineStageSearchControlProps
  extends Omit<ControlProps, "data"> {
  data: HubspotPipelineStageEntity | string | undefined;
  pipelineType: "deals" | "tickets";
  pipelineField: string;
}

const HubSpotPipelineStageSearchControl: React.FC<
  HubSpotPipelineStageSearchControlProps
> = ({
  data,
  enabled,
  handleChange,
  path,
  pipelineType,
  pipelineField,
  label,
}) => {
  const ctx = useJsonForms();

  const formData = ctx?.core?.data || {};

  // Extract ID from data (handles both string ID and entity object)
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("hubspot");

  // Get the pipelineId from the root form data (also handle entity objects)
  const pipelineIdRaw = formData?.[pipelineField];
  const pipelineId =
    typeof pipelineIdRaw === "string"
      ? pipelineIdRaw
      : pipelineIdRaw
        ? getEntityId(pipelineIdRaw as any)
        : null;

  // Fetch pipeline stages using the dedicated endpoint
  const { data: stagesData, isLoading: loading } = useHubSpotPipelineStages(
    {
      integrationConnectionId: integration?.id || "",
      objectType: pipelineType,
      pipelineId: pipelineId || "",
    },
    {
      enabled: Boolean(integration && pipelineId),
    },
  );

  // Get stages from the response
  const stages = stagesData || [];

  const selectedStage = useMemo(() => {
    return stages.find((s) => selectedId === s.data.id);
  }, [stages, selectedId]);

  const filteredStages = useMemo(() => {
    if (!searchQuery) return stages;
    return stages.filter((s) =>
      s.data.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [stages, searchQuery]);

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled || !pipelineId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedStage ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <List className="w-4! h-4!" />
                </div>
                <span className="truncate">{selectedStage.data.label}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                Select pipeline stage...
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedStage && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search stages..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No stages found.</CommandEmpty>
                  <CommandGroup>
                    {filteredStages.map((stage) => (
                      <CommandItem
                        key={stage.data.id}
                        value={stage.data.label}
                        onSelect={() => {
                          // Return full entity object
                          handleChange(path, stage);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                          <List className="w-4! h-4!" />
                        </div>
                        <span className="truncate">{stage.data.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const hubSpotPipelineStageTester = rankWith(
  5,
  optionIs("control", "hubspot_pipeline_stage"),
);

export const hubSpotPipelineDealStageTester = rankWith(
  5,
  optionsAre({
    control: "hubspot_pipeline_stage",
    pipeline_type: "deals",
  }),
);

export const hubSpotPipelineTicketStageTester = rankWith(
  5,
  optionsAre({
    control: "hubspot_pipeline_stage",
    pipeline_type: "tickets",
  }),
);

export default withJsonFormsControlProps((props: ControlProps) => {
  const { pipeline_type, pipeline_field } = props.uischema.options || {};

  if (!pipeline_type || !pipeline_field) {
    return null;
  }

  return (
    <HubSpotPipelineStageSearchControl
      {...props}
      pipelineType={pipeline_type}
      pipelineField={pipeline_field}
    />
  );
});
